(ns hai.notes.create
  "Standalone note creation in the expanded editor ([[hai.notes.editor]]): the
   universal bar, the notes wall, and the mobile add sheet open it on a fresh
   regular note that auto-saves as you type — the same behaviour as the home
   scratchpad, but the note never touches the pad. Mount [[overlay]] once at
   the app root."
  (:require
   [hai.entity.data :as entity-data]
   [hai.entity.staging :as staging]
   [hai.notes.data :as notes-data]
   [hai.notes.editor :as editor]
   [hai.notes.eql :as eql]
   [hai.notes.mutations :as mutations]
   [hai.notes.stacks :as stacks]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.web-app.viewport :as viewport]
   [reagent.core :as r]))

(def ^:private chips-scope
  "This host's staging scope ([[hai.entity.staging]]) — a session key rather
   than the note's uuid, which a fresh draft doesn't have yet."
  ::chips)

(defsub ::open? [db]
  (get db ::open? false))

(defsub ::note [db]
  (get db ::note))

(defsub ::tags [db]
  (-> (get-in db [::note :note/tags] [])
      (staging/merge-staged-tags (staging/staged db chips-scope :tags)
                                 (staging/removals db chips-scope :tags))))

(defsub ::link-urls [db]
  (-> (->> (get-in db [::note :note/activities])
           (keep :activity/url))
      (staging/merge-staged identity
                            (staging/staged db chips-scope :links)
                            (staging/removals db chips-scope :links))))

(defsub ::contacts [db]
  (-> (get-in db [::note :note/contacts] [])
      (staging/merge-staged :contact/uuid
                            (staging/staged db chips-scope :contacts)
                            (staging/removals db chips-scope :contacts))))

(defsub ::linked-notes [db]
  (-> (get-in db [::note :note/notes] [])
      (staging/merge-staged :note/uuid
                            (staging/staged db chips-scope :notes)
                            (staging/removals db chips-scope :notes))))

(defsub ::linked-tasks [db]
  (-> (get-in db [::note :note/tasks] [])
      (staging/merge-staged :task/uuid
                            (staging/staged db chips-scope :tasks)
                            (staging/removals db chips-scope :tasks))))

(defsub ::saving? [db]
  (get db ::saving? false))

(defsub ::generation [db]
  (get db ::generation 0))

(defn form-id
  "One form state per creation session — bumping the generation on open gives
   every session a blank form."
  [generation]
  [::form generation])

(defevent ::open [db _]
  (-> db
      (dissoc ::note ::save-request-id)
      (staging/clear chips-scope)
      (assoc ::open? true ::saving? false)
      (update ::generation (fnil inc 0))))

;; Refresh whichever note list the user is looking at, so the created note
;; shows without a manual reload — and nothing otherwise.
(defn- visible-list-refresh-fx [db]
  (case (get-in db [:routing/current-route :data :name])
    :timelines/view [[:dispatch [:timeline/load-entries]]]
    :notes/view     [[:dispatch [:table/resolve notes-data/table-id]]
                     [:dispatch [::notes-data/load-facets]]]
    []))

(defevent-fx ::close
  [{:keys [db]} _]
  (cond-> {:db (assoc db ::open? false)}
    (get-in db [::note :note/uuid]) (assoc :fx (visible-list-refresh-fx db))))

(defevent-fx ::save
  [{:keys [db]} {:keys [staged removal] :as payload}]
  ;; `:staged` — `{:family … :chip … :error-text …}` — is an optimistic chip
  ;; shown while this save is in flight; `:removal` — `{:family … :id …
  ;; :error-text …}` — one hidden while its detach is. Both are retired when
  ;; the response (which then carries the change) lands or fails.
  (let [note-uuid (get-in db [::note :note/uuid])]
    (cond
      ;; Nothing to create from yet.
      (and (nil? note-uuid) (mutations/empty-payload? payload))
      {:db db}

      :else
      (let [req-id (random-uuid)
            params (mutations/upsert-params {:note-uuid note-uuid :source :web-app}
                                            payload)]
        {:db (-> db
                 (assoc ::saving? true ::save-request-id req-id)
                 (cond-> staged (staging/stage chips-scope (:family staged) (:chip staged)))
                 (cond-> removal (staging/mark-removal chips-scope (:family removal) (:id removal))))
         :resolve (assoc (mutations/upsert-resolve params)
                         :request-id req-id
                         :store-in [::note]
                         :on-success-fx (cond-> [[:dispatch [::save-success]]]
                                          staged (conj [:dispatch [::staging/unstage chips-scope
                                                                   (:family staged) (:chip staged)]])
                                          removal (conj [:dispatch [::staging/unmark-removal chips-scope
                                                                    (:family removal) (:id removal)]]))
                         :on-error-fx (cond-> [[:dispatch [::save-error]]]
                                        staged (conj [:dispatch [::staging/add-failed chips-scope
                                                                 (:family staged) (:chip staged)
                                                                 (:error-text staged)]])
                                        removal (conj [:dispatch [::staging/remove-failed chips-scope
                                                                  (:family removal) (:id removal)
                                                                  (:error-text removal)]])))}))))

(defevent-fx ::save-success
  [{:keys [db]} _]
  ;; A pending autosave can land after the editor closed (the unmount flush);
  ;; refresh the visible list then, since ::close couldn't have.
  (cond-> {:db (-> db (assoc ::saving? false) (dissoc ::save-request-id))}
    (not (::open? db)) (assoc :fx (visible-list-refresh-fx db))))

(defevent ::save-error [db _]
  (-> db
      (assoc ::saving? false)
      (dissoc ::save-request-id)))

;; Removals are optimistic through [[hai.entity.staging]] marks, like the adds
;; riding [[::save]]: the chip hides immediately, the response reconciles via
;; store-in, and an error unhides it with a toast. Marks survive an in-flight
;; autosave's whole-note store-in, which the snapshot restores here previously
;; didn't.
(defn- marked-removal
  [db {:keys [family id error-text resolve]}]
  {:db (staging/mark-removal db chips-scope family id)
   :resolve (assoc resolve
                   :store-in [::note]
                   :on-success-fx [[:dispatch [::staging/unmark-removal chips-scope family id]]]
                   :on-error-fx [[:dispatch [::staging/remove-failed chips-scope family id error-text]]])})

(defevent-fx ::remove-link
  [{:keys [db]} link-url]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :links
                        :id link-url
                        :error-text "Could not remove link. Try again."
                        :resolve (mutations/remove-link-resolve note-uuid link-url)})))

(defevent-fx ::remove-contact
  [{:keys [db]} contact-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :contacts
                        :id contact-uuid
                        :error-text "Could not remove contact. Try again."
                        :resolve (mutations/remove-contact-resolve note-uuid contact-uuid)})))

(defevent-fx ::remove-note-link
  [{:keys [db]} linked-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :notes
                        :id linked-uuid
                        :error-text "Could not remove linked note. Try again."
                        :resolve (mutations/remove-note-resolve note-uuid linked-uuid)})))

(defevent-fx ::remove-task
  [{:keys [db]} task-uuid]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (marked-removal db {:family :tasks
                        :id task-uuid
                        :error-text "Could not remove linked task. Try again."
                        :resolve (mutations/remove-task-resolve note-uuid task-uuid)})))

;; Re-resolve the note in place — after the share dialog closes, so
;; freshly-added collaborators surface in the footer.
(defevent-fx ::refresh
  [{:keys [db]} _]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    (let [ident [:note/uuid note-uuid]]
      {:resolve {:query [{ident eql/note-eql}]
                 :transform-fn (fn [resp] (get resp ident))
                 :store-in [::note]}})))

(defevent-fx ::delete
  [{:keys [db]} _]
  (when-let [note-uuid (get-in db [::note :note/uuid])]
    ;; Optimistic: the editor closes now. An error toasts — the note still
    ;; exists, reachable from the wall. Cancel any in-flight autosave so its
    ;; response can't re-store the note after it's been deleted.
    (let [pending-save-id (::save-request-id db)]
      {:db (cond-> (-> db
                       (dissoc ::note ::save-request-id)
                       (staging/clear chips-scope)
                       (assoc ::open? false ::saving? false))
             pending-save-id (update :resolve/canceled-requests (fnil conj #{}) pending-save-id))
       :resolve (assoc (mutations/delete-resolve note-uuid)
                       :on-success-fx (visible-list-refresh-fx db)
                       :on-error-fx [[:dispatch [:toast/error {:text "Could not delete note. Try again."}]]])})))

(def extras-handlers
  "The creation note's tag/link save wiring for [[hai.notes.form/note-extras]].
   Adds ride [[::save]] with a staged optimistic chip."
  {:on-add-tag     (fn [tag]
                     (>e [::save (assoc (editor/tag-save-payload tag)
                                        :staged {:family :tags
                                                 :chip (staging/tag-chip tag)
                                                 :error-text "Could not add tag. Try again."})]))
   :on-remove-tag  (fn [tag]
                     (if-let [tag-uuid (:group/uuid tag)]
                       (>e [::save {:untag-uuids [tag-uuid]
                                    :removal {:family :tags
                                              :id tag-uuid
                                              :error-text "Could not remove tag. Try again."}}])
                       ;; ✕ on a chip still awaiting its create — retire it
                       ;; locally; the create's response restores server truth.
                       (>e [::staging/unstage chips-scope :tags tag])))
   :on-add-link    (fn [link-url]
                     (>e [::save {:link-urls [link-url]
                                  :staged {:family :links
                                           :chip link-url
                                           :error-text "Could not add link. Try again."}}]))
   :on-remove-link (fn [link-url] (>e [::remove-link link-url]))
   :on-add-contact    (fn [contact]
                        (>e [::save {:contact-uuids [(:contact/uuid contact)]
                                     :staged {:family :contacts
                                              :chip (staging/contact-chip contact)
                                              :error-text "Could not add contact. Try again."}}]))
   :on-remove-contact (fn [contact-uuid] (>e [::remove-contact contact-uuid]))
   :on-add-note       (fn [linked-note]
                        (>e [::save {:link-note-uuids [(:note/uuid linked-note)]
                                     :staged {:family :notes
                                              :chip (staging/note-chip linked-note)
                                              :error-text "Could not add linked note. Try again."}}]))
   :on-remove-note    (fn [linked-uuid] (>e [::remove-note-link linked-uuid]))
   :on-add-task       (fn [task]
                        (>e [::save {:task-uuids [(:task/uuid task)]
                                     :staged {:family :tasks
                                              :chip (staging/task-chip task)
                                              :error-text "Could not add linked task. Try again."}}]))
   :on-remove-task    (fn [task-uuid] (>e [::remove-task task-uuid]))
   :on-open-entity    entity-data/open-entity})

(defn overlay
  "The \"Creating note\" editor — the expanded modal on desktop, the
   full-screen editor on mobile. Mount once at the app root; opened via
   [[::open]]."
  []
  (r/with-let [!autosave (atom nil)]
    (let [open?        @(<s [::open?])
          mobile?      @(<s [::viewport/mobile?])
          note         @(<s [::note])
          tags         @(<s [::tags])
          link-urls    @(<s [::link-urls])
          contacts     @(<s [::contacts])
          linked-notes @(<s [::linked-notes])
          linked-tasks @(<s [::linked-tasks])
          saving?      @(<s [::saving?])
          generation   @(<s [::generation])
          editor-props {:title           "Creating note"
                        :note            note
                        :tags            tags
                        :link-urls       link-urls
                        :contacts        contacts
                        :linked-notes    linked-notes
                        :linked-tasks    linked-tasks
                        :saving?         saving?
                        :form-id         (form-id generation)
                        :!autosave       !autosave
                        :on-save-text    (fn [text] (>e [::save {:text text}]))
                        :extras-handlers extras-handlers
                        :on-share-closed #(>e [::refresh])
                        :on-close        #(>e [::close])
                        :on-delete       (fn []
                                           ;; Drop the pending autosave so the
                                           ;; unmount flush can't re-create the
                                           ;; note being deleted.
                                           (when-let [{:keys [discard!]} @!autosave]
                                             (discard!))
                                           (>e [::delete]))}]
      (if mobile?
        (when open?
          [editor/screen editor-props])
        [editor/dialog
         {:open?    open?
          :on-close #(>e [::close])
          :title    "Creating note"}
         (when open?
           [editor/modal-content editor-props])]))))
